import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { setDarkTheme, setLanguage, updateWidget, updatePhoneValidation } from '../Store/actions/customerInfoActions';
import countries from '../services/countries';
import languages from '../services/languages';
import parsePhoneNumber from 'libphonenumber-js';
import axios from 'axios';

interface PersonalInfo {
  Title: any;
  Name: any;
  'Highest Support Plan': any;
  'Email Address': any;
  'Renga ID': any;
  Phone: any;
  Country: any;
  Language: any;
  'Active Subscription Plan'?: any;
  'Value Add Action'?: any;
  offerEligibility?: any;
}

const ASPLIST = ['Business', 'Enterprise', 'Elite'];

const resolveAuthValue = (jcAuthData: any, key: string): string => {
  const entry = jcAuthData?.[key];
  if (entry == null) {
    return '';
  }
  if (typeof entry === 'string' || typeof entry === 'number') {
    return String(entry).trim();
  }
  if (typeof entry === 'object' && entry.value != null) {
    return String(entry.value).trim();
  }
  return '';
};

const buildCaseNumberField = (
  jcAuthData: any,
  ocAppData: any,
  userPersisted: any,
): Array<{ display: string; url: string }> => {
  const isEmailChannel =
    (resolveAuthValue(jcAuthData, 'channel') || '').toLowerCase() === 'email' ||
    ocAppData?.originForCurrentConversation === 'email';

  if (isEmailChannel) {
    return [];
  }

  // Voice cases are now created by the backend on /load (abandoned-call fix) and
  // returned directly on the persisted customer record, instead of via
  // contact-flow attributes - prefer that when present.
  if (userPersisted?.caseNumber || userPersisted?.caseUrl) {
    return [{
      display: userPersisted.caseNumber || userPersisted.caseUrl,
      url: userPersisted.caseUrl,
    }];
  }

  const caseNumber = resolveAuthValue(jcAuthData, 'case_number');
  const caseUrl = resolveAuthValue(jcAuthData, 'case_url');

  if (!caseNumber && !caseUrl) {
    return [];
  }

  return [{
    display: caseNumber || caseUrl,
    url: caseUrl,
  }];
};

const useAttributeData = (contextData: any, darkMode: any, isCIP: any, language: any) => {
  const dispatch = useDispatch();
  const { currentContactId, isConverstationActive, userPersisted, searchedUser, orderQuote, customerOfferList }: any = useSelector((state: any) => state.customerInfo);
  const { jcAuthData, ocPlatformData, ocAppData, jcMidConvData } = contextData;
  const phoneOrigin = ['outbound-phone', 'inbound-phone', 'bot-to-phone'];
  const isPhoneOrigin = phoneOrigin.includes(ocAppData.originForCurrentConversation);
  const allPhoneOrigin = ['outbound-phone', 'inbound-phone', 'bot-to-phone', 'chat-to-phone'];

  useEffect(() => {
    let isMounted = true;
    if (isMounted) {
      dispatch(setDarkTheme(darkMode));
      dispatch(setLanguage(language));
    }
    return () => {
      isMounted = false;
    };
  }, [darkMode, language]);
  // NOSONAR
  useEffect(() => {
    let isMounted = true;
    try {
      if (isMounted && jcAuthData && jcMidConvData && ocPlatformData) {
        const isAttributeAvailable = (attribute: string) => (attribute ? [attribute] : []);
        const isNameAvailable = jcAuthData.firstName ? [`${jcAuthData.firstName} ${jcAuthData.lastName}`] : [];
        const countryWithCode = jcAuthData.country ? jcAuthData.country : jcAuthData.countryCode ? jcAuthData.countryCode : jcAuthData.surfaceIpRegion;
        const country = countries[countryWithCode];
        const surfaceOrigin = jcAuthData?.surfaceOrigin ? [jcAuthData.surfaceOrigin] : [];
        const languageData = languages[`${jcAuthData.language ? jcAuthData.language : jcAuthData.languageCode}_` + `${countryWithCode}`];
        const isReturnCustomer = jcAuthData.returningCustomerType && !jcAuthData.returningCustomerType.toLowerCase().includes('none');
        const isReturnString = isReturnCustomer ? ['RETURN'] : [];
        const isCall = ocPlatformData.callInfo?.initialContactId && ocPlatformData.chatInfo.contactId;
        let intent: any = (jcAuthData.topic || '') + " > " + (jcAuthData.category || '');
        intent = intent === " > " ? [] : [intent];
        const isAllPhoneOrigin = allPhoneOrigin.includes(ocAppData.originForCurrentConversation);
        const { callInfo: { handoffCount: pHandoffCount = '' } = {} } = ocPlatformData;
        const { closedConversation = '', closedPhoneCall = '' } = ocAppData;
        const isActive = !(closedConversation && closedPhoneCall);
        const callHandoffCount =
          isActive && isAllPhoneOrigin
            ? pHandoffCount
              ? [pHandoffCount]
              : undefined
            : !isActive && isAllPhoneOrigin
              ? jcAuthData.handoffCount
                ? [jcAuthData.handoffCount]
                : undefined
              : undefined;
        console.log('CIP callHandoffCount', callHandoffCount, isActive, isAllPhoneOrigin, pHandoffCount);
        let chatId = [];
        let phoneId = [];
        if (isPhoneOrigin) {
          phoneId = isAttributeAvailable(ocPlatformData.chatInfo.contactId);
        } else if (isCall) {
          chatId = isAttributeAvailable(ocPlatformData.chatInfo.contactId);
          phoneId = isAttributeAvailable(ocPlatformData.callInfo.initialContactId);
        } else if (ocAppData.originForCurrentConversation === 'inbound-chat') {
          chatId = isAttributeAvailable(ocPlatformData.chatInfo.contactId);
          phoneId = isAttributeAvailable(jcAuthData.linkedConversationId);
        } else if (ocAppData.originForCurrentConversation === 'chat-to-phone') {
          chatId = isAttributeAvailable(jcAuthData.linkedConversationId);
          phoneId = isAttributeAvailable(ocPlatformData.chatInfo.contactId);
        }
        // if customerId is same as currentContactId then Anonymous should display
        const isAnonymous = jcAuthData.customerId === currentContactId;
        const rengaAnonymous = jcAuthData.customerId && !isAnonymous ? [jcAuthData.customerId] : ['Anonymous'];

        const aspSupportData = jcAuthData.aspSupportData;
        const aspRegion = jcAuthData.aspSupportRegion;
        if (JSON.parse(localStorage.getItem('fastFollowFlags')).ASP && ASPLIST.includes(aspSupportData)) {
          jcAuthData.support_entitlements = [{ highestASP: aspSupportData, aspRegion }];
        }

        console.log('SCIP/CIP QE', contextData, rengaAnonymous);
        const contextualInfo = {
          Title: 'Contextual Info',
          'Queue Path': jcAuthData?.queuePath?.split(',') || [],
          Website: isAttributeAvailable(jcAuthData.surfaceUrl),
          'CustomerOne Issue': jcAuthData.issueType ? [jcAuthData.issueType] : [],
          'CustomerOne Sub-Issue': jcAuthData.subissueType ? [jcAuthData.subissueType] : [],
          'Bot Intent': jcMidConvData.botIntent ? [jcMidConvData.botIntent] : isReturnString,
          'Bot SubIntent': jcMidConvData.botSubintent
            ? [jcMidConvData.botSubintent]
            : isReturnCustomer || jcAuthData.returningCustomerCaseId
              ? [`Case: ${jcAuthData.returningCustomerCaseId ? jcAuthData.returningCustomerCaseId : 'No Open Case'}`]
              : [],
          Product: jcMidConvData.botProductName ? [jcMidConvData.botProductName] : isAttributeAvailable(jcAuthData.subProductName),
          'Number of Transfers': callHandoffCount ? callHandoffCount : jcAuthData.transferCount ? [jcAuthData.transferCount] : ['0'],
          Origin: ocAppData?.originForCurrentConversation ? [ocAppData.originForCurrentConversation] : [],
        };
        console.log('CIP callHandoffCount', contextualInfo, callHandoffCount, callHandoffCount ?? jcAuthData.transferCount ? [jcAuthData.transferCount] : ['0']);
        const workflow: any = jcMidConvData.HostContextInfo ? JSON.parse(atob(jcMidConvData.HostContextInfo))[0] : '';
        const startTime: string = ocPlatformData.chatInfo.chatStartTime
          ? ocPlatformData.chatInfo.chatStartTime
          : ocPlatformData.callInfo.callStartTime
            ? ocPlatformData.callInfo.callStartTime
            : '';

        const caseNumberField = buildCaseNumberField(jcAuthData, ocAppData, userPersisted);

        const conversationInfo = {
          Title: 'Conversation Info',
          'Queue Path': jcAuthData?.queuePath?.split(',') || [],
          'Issue Path': intent || [],
          'Start Time': [moment(startTime).format('ddd MMM DD, YYYY hh:mma')],
          Device: isAttributeAvailable(jcAuthData.deviceFamily),
          Browser: jcAuthData.browser && jcAuthData.browserVersion ? [`${jcAuthData.browser} ${jcAuthData.browserVersion}`] : [],
          OS: isAttributeAvailable(jcAuthData.os),
          'Chat ID': chatId,
          'Phone ID': phoneId,
          'Case Number': caseNumberField,
          Channel : ocAppData?.originForCurrentConversation ? [ocAppData.originForCurrentConversation] : [],
          Origin: surfaceOrigin || []
        };

        if (isCIP) {
          console.log('CIP-isCIP', isCIP, userPersisted, searchedUser);
          const { linkedCustomer: { name = '', status = '', type = '', country = '', city = '', email = '', managerEmail = '', office = '', originalPhone = '', vip = '0', sensitivity = '0', isDirectorOrAbove = 'false' } = {}, entitlement = '' } = searchedUser?.linkedCustomer?.email ? searchedUser : userPersisted;
          console.log('CIP modifiedCustomer', ocAppData, customerOfferList);
          const personalInfo: any = {
            Title: 'Employee Info',
            'Full Name': name,
            'Emp Status': isAttributeAvailable(status),
            'Emp Type': isAttributeAvailable(type),
            'Country': isAttributeAvailable(country),
            'City': isAttributeAvailable(city),
            'Email': isAttributeAvailable(email),
            'Mgr Email': isAttributeAvailable(managerEmail),
            'Office': office ? [office] : ['N/A'],
            'Contact': !originalPhone || originalPhone === 'null' ? ['N/A'] : isAttributeAvailable(originalPhone),
            vip,
            sensitivity,
            isDirectorOrAbove,
          };
          if (customerOfferList[0]?.cctPro) personalInfo.offerEligibility = customerOfferList[0]?.cctPro === true ? ['CCT-Pro'] : ['---'];
          const CIP_data = [personalInfo,conversationInfo];
          dispatch(updateWidget(isCIP, CIP_data));
        } else {
          const { linkedCustomer: { firstName = '', lastName = '', email = '', rengaId = '', phone = '' } = {}, entitlement = '' } = userPersisted || {};
          const isrengaAnonymous = rengaId === currentContactId;
          const orderQuoteData = orderQuote || {};
          const splitEntitlementWithColen = jcAuthData.entitlementsList.split(';');
          const splitEntitlementWithcomma = jcAuthData.entitlementsList.split(',');
          console.log('SCIP linkedCustomer', userPersisted, workflow);
          //const phoneNumber = phone ? phone : jcAuthData.phone ? jcAuthData.phone : jcAuthData.phoneNumber ? jcAuthData.phoneNumber : '';          
          //const validatedPhoneNumber = getValidatedPhoneNumber(phoneNumber)
          const SCIP_data = [
            {
              Title: 'Personal Info',
              Name: firstName && lastName ? [`${firstName} ${lastName}`] : isNameAvailable,
              'Highest Support Plan': jcAuthData.support_entitlements ? jcAuthData.support_entitlements : [],
              'Email Address': email ? [email] : isAttributeAvailable(jcAuthData.email),
              'Renga ID': rengaId && !isrengaAnonymous ? [rengaId] : rengaAnonymous,
              //Phone: validatedPhoneNumber.length ? [validatedPhoneNumber] : [],
              Phone: phone ? [phone] : jcAuthData.phone ? [jcAuthData.phone] : jcAuthData.phoneNumber ? [jcAuthData.phoneNumber] : [],
              // jcAuthData.email && jcAuthData.customerType.toLowerCase() === 'unauthenticated'
              Country: isAttributeAvailable(country || jcAuthData.surfaceIpRegion),
              Language: [languageData || languages[`${jcAuthData.language}_${jcAuthData.region}`] || 'English'],
              'Active Subscription Plan': entitlement
                ? entitlement
                : jcAuthData.entitlementsList && jcAuthData.entitlementsList.includes(';') && splitEntitlementWithColen.length > 0 && splitEntitlementWithColen[0]
                  ? splitEntitlementWithColen
                  : splitEntitlementWithcomma.length > 0 && splitEntitlementWithcomma[0]
                    ? jcAuthData.entitlementsList.split(',')
                    : [],
            },
            {
              Title: 'Quote / Order Details',
              Order: orderQuoteData.Order,
              Quote: orderQuoteData.Quote,
            },
            contextualInfo,
            conversationInfo,
            {
              Title: 'Customer Info',
            },
            {
              Title: 'Customer Interaction Summary',
            },
          ];
          dispatch(updateWidget(isCIP, SCIP_data));
        }
      }
    } catch (error) {
      console.log('SCIP useAttributeData error', error);
    }
    return () => {
      isMounted = false;
    };
  }, [currentContactId, userPersisted, ocAppData, searchedUser, jcAuthData?.case_number, jcAuthData?.case_url]);
};

export default useAttributeData;
